import { useEffect, useState } from "react";
import { Mail, ShieldCheck, CreditCard, TrendingUp } from "lucide-react";
import { api } from "../lib/api";

/**
 * AdminRecoveryStats — 7-day overview of the KYC + abandoned-booking
 * nudge loops: emails sent, conversions back to a "won" state, and
 * recovered USD revenue from booking flows.
 *
 * Window selector lets admins pull 7d / 14d / 30d / 90d.
 */
export default function AdminRecoveryStats() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const { data } = await api.get(`/admin/recovery-stats?days=${days}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || "Failed to load");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [days]);

  const windowOpts = [7, 14, 30, 90];

  return (
    <div className="space-y-6" data-testid="admin-recovery-stats">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="label-mystic text-secondary">Drop-off Recovery</div>
          <h2 className="font-serif text-3xl mt-1">Nudge Emails — last {days} days</h2>
          <p className="text-xs text-foreground/55 mt-1">
            Tracks the impact of KYC and abandoned-booking reminder loops.
          </p>
        </div>
        <div className="flex gap-1">
          {windowOpts.map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-[11px] uppercase tracking-wider rounded-sm border ${
                days === d
                  ? "border-secondary text-secondary bg-secondary/10"
                  : "border-foreground/15 text-foreground/60 hover:text-foreground hover:border-foreground/40"
              }`}
              data-testid={`recovery-window-${d}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-sm text-foreground/55">Loading…</div>}
      {err && <div className="text-sm text-destructive">{err}</div>}

      {!loading && !err && data && (
        <div className="grid sm:grid-cols-2 gap-4">
          {/* ── KYC reminders ─── */}
          <div className="parchment p-5 space-y-3" data-testid="recovery-kyc-card">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-secondary" strokeWidth={2} />
              <span className="label-mystic text-secondary">KYC verifications</span>
            </div>
            <div className="flex items-end justify-between flex-wrap gap-3">
              <Stat label="Reminders sent" value={data.kyc.remindersSent} />
              <Stat label="Recovered → approved" value={data.kyc.recovered} accent />
              <Stat label="Still pending" value={data.kyc.stillPending} muted />
            </div>
            <ConversionBar pct={data.kyc.conversionPct} testid="recovery-kyc-bar" />
          </div>

          {/* ── Abandoned bookings ─── */}
          <div className="parchment p-5 space-y-3" data-testid="recovery-bookings-card">
            <div className="flex items-center gap-2">
              <CreditCard size={16} className="text-primary" strokeWidth={2} />
              <span className="label-mystic text-primary">Service bookings</span>
            </div>
            <div className="flex items-end justify-between flex-wrap gap-3">
              <Stat label="Reminders sent" value={data.bookings.remindersSent} />
              <Stat label="Recovered → paid" value={data.bookings.recovered} accent />
              <Stat
                label="Recovered revenue"
                value={`$${(data.bookings.recoveredRevenueUsd || 0).toFixed(2)}`}
                icon={<TrendingUp size={11} />}
              />
            </div>
            <ConversionBar pct={data.bookings.conversionPct} testid="recovery-bookings-bar" />
          </div>
        </div>
      )}

      {!loading && !err && data && (
        <div className="text-[11px] text-foreground/40 flex items-center gap-1.5 mt-2">
          <Mail size={11} /> Conversion % counts users who reached the terminal "won" state
          (KYC approved / booking paid) AFTER receiving the nudge within the window.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = false, muted = false, icon = null }) {
  return (
    <div className="min-w-[100px]">
      <div className={`text-[10px] uppercase tracking-[0.2em] flex items-center gap-1 ${muted ? "text-foreground/40" : "text-foreground/55"}`}>
        {icon}
        {label}
      </div>
      <div className={`font-serif text-3xl mt-0.5 ${accent ? "text-secondary" : muted ? "text-foreground/55" : "text-foreground"}`}>
        {value}
      </div>
    </div>
  );
}

function ConversionBar({ pct, testid }) {
  const clamped = Math.max(0, Math.min(100, pct || 0));
  return (
    <div className="pt-2 space-y-1.5">
      <div className="flex justify-between text-[11px] text-foreground/55 uppercase tracking-wider">
        <span>Conversion</span>
        <span data-testid={testid}>{pct.toFixed(1)}%</span>
      </div>
      <div className="h-1.5 bg-foreground/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-secondary transition-all"
          style={{ width: `${clamped}%` }}
        />
      </div>
    </div>
  );
}
